import { useState, useEffect } from "react";
import PlanCard from "components/plan-card";
import ToggleSwitch from "components/toggle-switch";
import convertToCurrencyAmount from "utils/methods/convertToCurrencyAmount";
import { ScaleLoader } from "react-spinners";
import { Check, Zap, Building2, Flame } from "lucide-react";
import {
  EOFY_SALE_END_DATE,
  ORIGINAL_PRICES,
  EOFY_SALE_PRICES,
  EOFY_DISCOUNT_LABEL,
  SHOW_EOFY_BANNER,
} from "enums/index";

// ─── EOFY Countdown Hook ────────────────────────────────────────────────────
function useEofyCountdown() {
  const calculateTimeLeft = () => {
    const diff = EOFY_SALE_END_DATE - new Date();
    if (diff <= 0) return null;
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / (1000 * 60)) % 60),
      seconds: Math.floor((diff / 1000) % 60),
    };
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft);

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(calculateTimeLeft()), 1000);
    return () => clearInterval(timer);
  }, []);

  return timeLeft;
}

// ─── Compact inline countdown — sits inside each paid card ─────────────────
function InlineCountdown({ timeLeft }) {
  if (!timeLeft) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <div
      className="rounded-ns px-2 py-1.5 mb-3"
      style={{ background: "rgb(var(--ns-warn-rgb) / 0.12)", border: "1px solid var(--ns-warn)" }}
    >
      <div className="flex items-center justify-center gap-1 mb-1">
        <Flame className="h-3 w-3 text-warn" />
        <span className="text-[10px] font-bold uppercase tracking-wide text-warn">
          EOFY Sale ends in
        </span>
      </div>
      <div className="flex items-center justify-center font-mono text-xs font-bold text-warn">
        <span>{pad(timeLeft.days)}d</span>
        <span className="opacity-40 mx-0.5">:</span>
        <span>{pad(timeLeft.hours)}h</span>
        <span className="opacity-40 mx-0.5">:</span>
        <span>{pad(timeLeft.minutes)}m</span>
        <span className="opacity-40 mx-0.5">:</span>
        <span>{pad(timeLeft.seconds)}s</span>
      </div>
    </div>
  );
}

// ─── Price block with optional strikethrough ────────────────────────────────
function PriceDisplay({ originalPrice, salePrice, isAnnual }) {
  const showSale = SHOW_EOFY_BANNER;
  return (
    <div className="flex flex-col items-center">
      {showSale && (
        <span className="text-xs text-muted line-through leading-none mb-0.5">
          {originalPrice}
        </span>
      )}
      <div className="flex items-baseline gap-0.5">
        <span className="text-xl font-bold text-ink">
          {showSale ? salePrice : originalPrice}
        </span>
        <span className="text-xs text-muted">/mo</span>
      </div>
      {isAnnual && (
        <p className="text-xs text-muted mt-0.5">billed annually</p>
      )}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────
const PlanDetails = ({
  loading,
  userUsageData,
  subscriptionData,
  handleClickGetStarted,
  products: stripeProducts,
}) => {
  const [isAnnual, setIsAnnual] = useState(true);
  const timeLeft = useEofyCountdown();

  if (!stripeProducts) {
    return (
      <div className="flex flex-col items-center justify-center h-96 w-full">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-rule border-t-accent mb-4"></div>
        <p className="text-sm text-muted">Loading plans...</p>
      </div>
    );
  }

  const handleTogglePlan = () => setIsAnnual((p) => !p);

  const handleFilteredDisplay = (variation) => {
    if (isAnnual && variation.recurring?.interval === "year") return true;
    if (!isAnnual && variation.recurring?.interval === "month") return true;
  };

  const finalStripeProducts = [];
  Object.keys(stripeProducts).forEach((el) => {
    finalStripeProducts.push(...stripeProducts[el]);
  });

  const isCurrentPlan = (currentPlanId) => {
    if (
      !subscriptionData ||
      !subscriptionData.planDuration ||
      !subscriptionData.planDuration.stripePriceId
    )
      return false;
    return (
      subscriptionData.status !== "canceled" &&
      subscriptionData.planDuration.stripePriceId === currentPlanId
    );
  };

  const isOnBasicPlan =
    userUsageData?.isFreeTrial ||
    !subscriptionData ||
    !subscriptionData.planDuration ||
    subscriptionData.status === "canceled";

  const discountBadgeLabel =
    SHOW_EOFY_BANNER && timeLeft ? EOFY_DISCOUNT_LABEL : "Save 20%";

  return (
    <div className="w-full p-4 animate-fade-in">
      {/* Header Section */}
      <div className="text-center mb-4">
        <h2 className="text-xl font-bold text-ink mb-2">
          Choose Your Plan
        </h2>
        <p className="text-sm text-muted">
          Select the perfect plan for your practice
        </p>
      </div>

      {/* Toggle Section */}
      <div className="mb-4 flex justify-center items-center">
        <div className="inline-flex items-center gap-3 bg-shade-50 rounded-full px-5 py-2.5 border border-rule">
          <span
            className={`text-sm font-medium transition-colors duration-200 ${
              !isAnnual ? "text-ink" : "text-muted"
            }`}
          >
            Monthly
          </span>
          <ToggleSwitch onToggle={handleTogglePlan} active={isAnnual} />
          <span
            className={`text-sm font-medium transition-colors duration-200 ${
              isAnnual ? "text-ink" : "text-muted"
            }`}
          >
            Annual
          </span>
          {isAnnual && (
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold text-ink bg-shade-50 animate-scale-in">
              {discountBadgeLabel}
            </span>
          )}
        </div>
      </div>

      {/* Plans Grid - Responsive */}
      <div className="overflow-x-auto sm:overflow-visible pb-4">
        <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-fit sm:mx-auto">
          {/* Free / Basic Plan */}
          {isOnBasicPlan && (
            <div
              className="w-full sm:w-56 sm:flex-shrink-0 animate-slide-up"
              style={{ animationDelay: "0ms" }}
            >
              <div className="relative bg-white rounded-ns border-2 p-4   transition-all duration-300 h-full flex flex-col border-rule-strong">
                <div className="text-center mb-4 pt-2">
                  <div className="inline-flex items-center justify-center w-8 h-8 bg-white rounded-full mb-3">
                    <Zap className="h-4 w-4 text-accent" />
                  </div>
                  <h3 className="text-base font-bold text-ink mb-2">Basic</h3>
                  <div className="mb-1">
                    <span className="text-xl font-bold text-ink">AUD 0</span>
                  </div>
                  <p className="text-xs text-muted">Free forever</p>
                </div>

                <div className="flex-1 space-y-2 mb-4">
                  {[
                    "60 sessions",
                    "Unlimited notes generation",
                    "Unlimited templates",
                    "Unlimited Custom Templates",
                    "Dictate",
                  ].map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-xs text-ink">{feature}</span>
                    </div>
                  ))}
                </div>

                <button
                  disabled
                  className="w-full py-2 px-4 bg-shade-50 text-muted rounded-ns font-semibold text-sm cursor-not-allowed"
                >
                  Current Plan
                </button>
              </div>
            </div>
          )}

          {/* Paid Plans */}
          {finalStripeProducts
            .filter((el) => handleFilteredDisplay(el))
            .map((el, index) => {
              const isPro = el.metadata?.variationName
                ?.toLowerCase()
                .includes("pro");
              const isCurrent = isCurrentPlan(el.id);
              const delayIndex = isOnBasicPlan ? index + 1 : index;

              const originalPrice = isAnnual
                ? ORIGINAL_PRICES.annual
                : ORIGINAL_PRICES.monthly;
              const salePrice = isAnnual
                ? EOFY_SALE_PRICES.annual
                : EOFY_SALE_PRICES.monthly;

              return (
                <div
                  key={el.id}
                  className="w-full sm:w-56 sm:flex-shrink-0 animate-slide-up"
                  style={{ animationDelay: `${delayIndex * 100}ms` }}
                >
                  <div
                    className={`relative bg-white rounded-ns border-2 p-4   transition-all duration-300 h-full flex flex-col ${
                      isCurrent
                        ? "border-accent"
                        : isPro
                        ? "border-accent"
                        : "border-accent hover:border-accent-light"
                    }`}
                  >
                    {/* Plan Header */}
                    <div className="text-center mb-3 pt-2">
                      <div
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-full mb-3 ${
                          isPro
                            ? "bg-accent"
                            : "bg-shade-50"
                        }`}
                      >
                        <Zap
                          className={`h-4 w-4 ${
                            isPro ? "text-white" : "text-muted"
                          }`}
                        />
                      </div>
                      <h3 className="text-base font-bold text-ink mb-2">
                        {el.metadata?.variationName}
                      </h3>

                      {/* Price with optional strikethrough */}
                      <PriceDisplay
                        originalPrice={originalPrice}
                        salePrice={salePrice}
                        isAnnual={isAnnual}
                      />
                    </div>

                    {/* ── Inline EOFY countdown inside the card ── */}
                    {SHOW_EOFY_BANNER && (
                      <InlineCountdown timeLeft={timeLeft} />
                    )}

                    {/* Features */}
                    <div className="flex-1 space-y-2 mb-4">
                      {(
                        !el.metadata?.variationFeatures
                          ? []
                          : el.metadata.variationFeatures.split(",")
                      ).map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                          <span className="text-xs text-ink">
                            {feature.trim()}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Button */}
                    <button
                      onClick={(e) => handleClickGetStarted(e, el.id)}
                      disabled={loading === el.id || isCurrent}
                      className={`w-full py-2 px-4 rounded-ns font-semibold text-sm transition-all duration-200 transform  active:scale-95 disabled:transform-none disabled:cursor-not-allowed relative ${
                        isCurrent
                          ? "bg-shade-50 text-white"
                          : isPro
                          ? "bg-accent text-white "
                          : "bg-accent text-white "
                      } hover:bg-deep`}
                    >
                      <span
                        className={
                          loading === el.id ? "opacity-0" : "opacity-100"
                        }
                      >
                        {isCurrent ? "Current Plan" : "Get Started"}
                      </span>
                      {loading === el.id && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <ScaleLoader loading color="white" height={12} />
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}

          {/* Enterprise Plan */}
          <div
            className="w-full sm:w-56 sm:flex-shrink-0 animate-slide-up"
            style={{
              animationDelay: `${
                (isOnBasicPlan
                  ? finalStripeProducts.filter(handleFilteredDisplay).length + 1
                  : finalStripeProducts.filter(handleFilteredDisplay).length) *
                100
              }ms`,
            }}
          >
            <div className="relative bg-shade-50 rounded-ns border-2 border-rule-strong p-4   transition-all duration-300 h-full flex flex-col">
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-8 h-8 bg-rule rounded-full mb-3">
                  <Building2 className="h-4 w-4 text-muted" />
                </div>
                <h3 className="text-base font-bold text-ink mb-2">
                  Enterprise
                </h3>
                <div className="mb-1">
                  <span className="text-xl font-bold text-ink">Custom</span>
                </div>
                <p className="text-xs text-muted">Tailored pricing</p>
              </div>

              <div className="flex-1 space-y-2 mb-4">
                {[
                  "Unlimited sessions",
                  "Unlimited notes",
                  "Custom templates",
                  "Priority support",
                  "Team collaboration",
                ].map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-ink">{feature}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() =>
                  window.open(
                    "https://www.nirvascribe.com/contact-us",
                    "_blank"
                  )
                }
                className="w-full py-2 px-4 bg-accent text-white rounded-ns font-semibold text-sm  transition-all duration-200 transform  active:scale-95 hover:bg-deep"
              >
                Contact Sales
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Section */}
      <div className="mt-4 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-shade-50 border border-rule rounded-full">
          <Check className="h-4 w-4 text-accent" />
          <span className="text-xs font-medium text-deep">
            Cancel anytime • No hidden fees
          </span>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.8); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fade-in { animation: fade-in 0.5s ease-out forwards; }
        .animate-slide-up { animation: slide-up 0.6s ease-out both; }
        .animate-scale-in { animation: scale-in 0.3s ease-out forwards; }
      `}</style>
    </div>
  );
};


export default PlanDetails;